// frontend/src/components/Login.tsx
import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

import { API_BASE_URL } from '../config/api';


const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reloginHint = searchParams.get('relogin') === '1';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [loading, setLoading] = useState(false);
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorDetails('');
    setLoading(true);

    try {
      await login({ username, password });
      navigate('/');
    } catch (err: any) {
      console.error('Login error:', err);
      
      // טיפול בשגיאות מהשרת
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // טיפול בשגיאות ספציפיות לפי סוג השגיאה
        if (errorData.pending_approval) {
          setError('חשבון ממתין לאישור');
          setErrorDetails('חשבונך נרשם בהצלחה אך ממתין לאישור מנהל המערכת. אנא המתן עד שחשבונך יאושר.');
        } else if (errorData.inactive) {
          setError('חשבון הושבת');
          setErrorDetails('חשבונך הושבת על ידי מנהל המערכת. אנא פנה למנהל.');
        } else if (errorData.auth_failed) {
          setError('שגיאה בהתחברות');
          setErrorDetails('שם המשתמש או הסיסמה שגויים. אנא נסה שוב.');
        } else if (errorData.detail) {
          setError('שגיאה בהתחברות');
          setErrorDetails(errorData.detail);
        } else if (errorData.username) {
          // שגיאות שדה ספציפיות - שם משתמש
          const usernameError = Array.isArray(errorData.username) ? errorData.username[0] : errorData.username;
          setError('שגיאה בהתחברות');
          setErrorDetails(usernameError);
        } else if (errorData.password) {
          // שגיאות שדה ספציפיות - סיסמה
          const passwordError = Array.isArray(errorData.password) ? errorData.password[0] : errorData.password;
          setError('שגיאה בהתחברות');
          setErrorDetails(passwordError);
        } else if (errorData.non_field_errors) {
          setError('שגיאה בהתחברות');
          setErrorDetails(Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : errorData.non_field_errors);
        } else {
          setError('שגיאה בהתחברות');
          setErrorDetails('שם המשתמש או הסיסמה שגויים. אנא נסה שוב.');
        }
      } else if (err.message) {
        setError('שגיאה בהתחברות');
        if (err.message.includes('ממתין לאישור')) {
          setErrorDetails('חשבונך נרשם בהצלחה אך ממתין לאישור מנהל המערכת.');
        } else if (err.message.includes('הושבת')) {
          setErrorDetails('חשבונך הושבת על ידי מנהל המערכת. אנא פנה למנהל.');
        } else {
          setErrorDetails(err.message);
        }
      } else {
        setError('שגיאה בהתחברות');
        setErrorDetails('אנא נסה שוב או פנה למנהל המערכת.');
      }
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <Container className="mt-5" style={{ maxWidth: '500px', direction: 'rtl' }}>
      <Card>
        <Card.Header as="h3" className="text-center">
          התחברות למערכת
        </Card.Header>
        <Card.Body>
          {reloginHint && (
            <Alert variant="info" className="mb-3">
              פג תוקף ההתחברות או שאין תקשורת תקינה עם השרת. התחבר שוב כדי להמשיך.
            </Alert>
          )}
          {error && (
            <Alert variant="danger">
              <strong>{error}</strong>
              {errorDetails && <><br /><small>{errorDetails}</small></>}
            </Alert>
          )}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>שם משתמש</Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                autoComplete="username"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>סיסמה</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
                       <Button
                         variant="outline-secondary"
                         onClick={() => setShowPassword(!showPassword)}
                         disabled={loading}
                       >
                         {showPassword ? <i className="fas fa-eye-slash"></i> : <i className="fas fa-eye"></i>}
                       </Button>
              </InputGroup>
            </Form.Group>

            <div className="text-end mb-3">
              <Link to="/forgot-password" className="text-muted small">
                שכחתי סיסמה
              </Link>
            </div>

            <Button
              variant="primary"
              type="submit"
              className="w-100 mb-3"
              disabled={loading}
            >
              {loading ? 'מתחבר...' : 'התחבר'}
            </Button>
          </Form>

          

          <div className="text-center">
            <p className="mb-2">אין לך חשבון?</p>
            <Link to="/register">
              <Button variant="outline-primary" className="w-100">
                הרשמה
              </Button>
            </Link>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Login;
